import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Area, AreaChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { api } from '@/utils/api';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import {
  ArrowUpRight,
  ArrowDownRight,
  BedDouble,
  Building2,
  Home,
  IndianRupee,
  Plus,
  Receipt,
  TrendingDown,
  TrendingUp,
  Wallet
} from 'lucide-react';
import Layout from '@/components/Layout';
import MonthCalendarCard from '@/components/MonthCalendarCard';

const formatCurrency = (value) => `₹${Number(value || 0).toLocaleString('en-IN')}`;
const toMonthParam = (date) => `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;

export default function Dashboard() {
  const [pgs, setPgs] = useState([]);
  const [selectedPg, setSelectedPg] = useState('');
  const [selectedMonth, setSelectedMonth] = useState(new Date(new Date().getFullYear(), new Date().getMonth(), 1));
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    fetchPgs();
  }, []);

  useEffect(() => {
    if (selectedPg) {
      fetchOverview();
    }
  }, [selectedPg, selectedMonth]);

  const fetchPgs = async () => {
    try {
      const response = await api.getPGs();
      setPgs(response.data);
      if (response.data.length > 0) {
        setSelectedPg(response.data[0].id);
      } else {
        setLoading(false);
      }
    } catch (error) {
      toast.error('Failed to fetch PGs');
      setLoading(false);
    }
  };

  const fetchOverview = async () => {
    try {
      setLoading(true);
      const response = await api.getDashboardFinancial(selectedPg, toMonthParam(selectedMonth));
      setOverview(response.data);
    } catch (error) {
      toast.error(error.message || 'Failed to fetch dashboard overview');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </Layout>
    );
  }

  if (pgs.length === 0) {
    return (
      <Layout>
        <div className="flex flex-col items-center justify-center h-[calc(100vh-200px)] px-4">
          <div className="w-20 h-20 bg-primary/10 rounded-full flex items-center justify-center mb-6">
            <Home className="w-10 h-10 text-primary" />
          </div>
          <h2 className="text-2xl font-bold text-slate-900 mb-2">Add your first PG</h2>
          <p className="text-slate-600 text-center mb-6 max-w-md">
            Start with one property and this dashboard will turn into your monthwise profit, expense, and rent command center.
          </p>
          <Button
            onClick={() => navigate('/setup')}
            className="h-12 px-8 rounded-xl bg-primary hover:bg-primary/90 text-white font-semibold shadow-sm"
            data-testid="setup-pg-button"
          >
            <Plus className="w-5 h-5 mr-2" />
            Setup Your PG
          </Button>
        </div>
      </Layout>
    );
  }

  const monthProfit = overview?.month_profit || 0;
  const fyProfit = overview?.financial_year_profit || 0;
  const monthProfitPositive = monthProfit >= 0;
  const fyProfitPositive = fyProfit >= 0;

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-slate-900 font-heading">Dashboard</h1>
            <p className="text-slate-600 mt-1">Monthwise business overview for your selected PG</p>
          </div>

          <div className="flex flex-col sm:flex-row gap-3">
            <select
              value={selectedPg}
              onChange={(e) => setSelectedPg(Number(e.target.value))}
              className="h-11 px-4 rounded-lg border border-slate-300 bg-white focus:ring-2 focus:ring-primary/20 focus:border-primary min-w-[220px]"
              data-testid="pg-selector"
            >
              {pgs.map((pg) => (
                <option key={pg.id} value={pg.id}>{pg.name}</option>
              ))}
            </select>
            <Button
              type="button"
              variant="outline"
              className="h-11 rounded-lg"
              onClick={() => navigate('/pgs')}
            >
              <Building2 className="w-4 h-4 mr-2" />
              Manage PGs
            </Button>
          </div>
        </div>

        {overview && (
          <>
            <div className="grid grid-cols-1 xl:grid-cols-[360px,1fr] gap-6">
              <MonthCalendarCard
                title="Business Calendar"
                subtitle="Pick any month to switch the dashboard"
                selectedMonth={selectedMonth}
                onMonthChange={setSelectedMonth}
                accentClassName="from-emerald-100 via-white to-blue-50"
              />

              <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
                <div className="bg-gradient-to-r from-slate-950 via-slate-900 to-emerald-900 p-6 text-white">
                  <p className="text-sm uppercase tracking-[0.22em] text-emerald-200 mb-2">{overview.selected_month_label}</p>
                  <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4">
                    <div>
                      <h2 className="text-3xl font-bold mb-2">
                        {monthProfitPositive ? 'Profit is ahead this month' : 'Loss needs attention this month'}
                      </h2>
                      <p className="text-slate-300 max-w-2xl">
                        Actual rent collected minus logged expenses, based on your payment and expense entries for the selected month.
                      </p>
                    </div>
                    <div className={`rounded-2xl px-5 py-4 ${monthProfitPositive ? 'bg-emerald-400/15' : 'bg-rose-400/15'}`}>
                      <p className="text-sm text-slate-300 mb-1">Net for month</p>
                      <p className="text-3xl font-bold">{formatCurrency(monthProfit)}</p>
                    </div>
                  </div>
                </div>

                <div className="p-6 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
                  <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4">
                    <div className="flex items-center justify-between mb-3">
                      <Wallet className="w-5 h-5 text-emerald-600" />
                      <ArrowUpRight className="w-4 h-4 text-emerald-600" />
                    </div>
                    <p className="text-sm font-semibold text-emerald-800">Rent Collected</p>
                    <p className="text-2xl font-bold text-emerald-700 mt-1">{formatCurrency(overview.month_rent_collected)}</p>
                  </div>

                  <div className="rounded-xl border border-rose-200 bg-rose-50 p-4">
                    <div className="flex items-center justify-between mb-3">
                      <TrendingDown className="w-5 h-5 text-rose-600" />
                      <ArrowDownRight className="w-4 h-4 text-rose-600" />
                    </div>
                    <p className="text-sm font-semibold text-rose-800">Expenses Logged</p>
                    <p className="text-2xl font-bold text-rose-700 mt-1">{formatCurrency(overview.month_expenses)}</p>
                  </div>

                  <div className="rounded-xl border border-blue-200 bg-blue-50 p-4">
                    <div className="flex items-center justify-between mb-3">
                      <IndianRupee className="w-5 h-5 text-blue-600" />
                      <Receipt className="w-4 h-4 text-blue-600" />
                    </div>
                    <p className="text-sm font-semibold text-blue-800">Expected Monthly Rent</p>
                    <p className="text-2xl font-bold text-blue-700 mt-1">{formatCurrency(overview.expected_monthly_rent)}</p>
                  </div>

                  <div className="rounded-xl border border-amber-200 bg-amber-50 p-4">
                    <div className="flex items-center justify-between mb-3">
                      <BedDouble className="w-5 h-5 text-amber-600" />
                      <TrendingUp className="w-4 h-4 text-amber-600" />
                    </div>
                    <p className="text-sm font-semibold text-amber-800">Occupancy</p>
                    <p className="text-2xl font-bold text-amber-700 mt-1">{overview.occupancy_rate}%</p>
                    <p className="text-xs text-amber-700/80 mt-1">{overview.occupied_beds} of {overview.total_beds} beds occupied</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-[1.35fr,0.95fr] gap-6">
              <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h2 className="text-xl font-bold text-slate-900 font-heading">Financial Year Trend</h2>
                    <p className="text-sm text-slate-500">{overview.financial_year_label} monthwise collection vs expense</p>
                  </div>
                  <div className={`rounded-full px-3 py-1 text-sm font-semibold ${fyProfitPositive ? 'bg-emerald-100 text-emerald-700' : 'bg-rose-100 text-rose-700'}`}>
                    FY Net: {formatCurrency(overview.financial_year_profit)}
                  </div>
                </div>

                <div className="h-[320px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={overview.monthly_trend}>
                      <defs>
                        <linearGradient id="rentFill" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#0f766e" stopOpacity={0.35} />
                          <stop offset="95%" stopColor="#0f766e" stopOpacity={0.02} />
                        </linearGradient>
                        <linearGradient id="expenseFill" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#dc2626" stopOpacity={0.22} />
                          <stop offset="95%" stopColor="#dc2626" stopOpacity={0.02} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
                      <XAxis dataKey="label" tick={{ fill: '#64748b', fontSize: 12 }} tickLine={false} axisLine={false} />
                      <YAxis tick={{ fill: '#64748b', fontSize: 12 }} tickLine={false} axisLine={false} />
                      <Tooltip formatter={(value) => formatCurrency(value)} />
                      <Area type="monotone" dataKey="rent_collected" stroke="#0f766e" fill="url(#rentFill)" strokeWidth={3} />
                      <Area type="monotone" dataKey="expenses" stroke="#dc2626" fill="url(#expenseFill)" strokeWidth={2.5} />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </div>

              <div className="space-y-6">
                <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm">
                  <h2 className="text-xl font-bold text-slate-900 font-heading mb-4">Financial Year Snapshot</h2>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between rounded-xl bg-slate-50 px-4 py-3">
                      <span className="text-sm font-medium text-slate-600">Rent collected</span>
                      <span className="text-lg font-bold text-slate-900">{formatCurrency(overview.financial_year_rent_collected)}</span>
                    </div>
                    <div className="flex items-center justify-between rounded-xl bg-slate-50 px-4 py-3">
                      <span className="text-sm font-medium text-slate-600">Expenses</span>
                      <span className="text-lg font-bold text-slate-900">{formatCurrency(overview.financial_year_expenses)}</span>
                    </div>
                    <div className={`flex items-center justify-between rounded-xl px-4 py-4 ${fyProfitPositive ? 'bg-emerald-50 border border-emerald-200' : 'bg-rose-50 border border-rose-200'}`}>
                      <span className="text-sm font-semibold text-slate-700">Net profit / loss</span>
                      <span className={`text-xl font-bold ${fyProfitPositive ? 'text-emerald-700' : 'text-rose-700'}`}>
                        {formatCurrency(overview.financial_year_profit)}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm">
                  <h2 className="text-xl font-bold text-slate-900 font-heading mb-4">Operational Pulse</h2>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="rounded-xl bg-slate-50 p-4">
                      <p className="text-sm text-slate-500 mb-1">Active Tenants</p>
                      <p className="text-2xl font-bold text-slate-900">{overview.active_tenants}</p>
                    </div>
                    <div className="rounded-xl bg-slate-50 p-4">
                      <p className="text-sm text-slate-500 mb-1">Total Dues</p>
                      <p className="text-2xl font-bold text-rose-600">{formatCurrency(overview.total_dues)}</p>
                    </div>
                    <div className="rounded-xl bg-slate-50 p-4">
                      <p className="text-sm text-slate-500 mb-1">Advance Balance</p>
                      <p className="text-2xl font-bold text-emerald-600">{formatCurrency(overview.total_advance)}</p>
                    </div>
                    <div className="rounded-xl bg-slate-50 p-4">
                      <p className="text-sm text-slate-500 mb-1">Selected Month</p>
                      <p className="text-lg font-bold text-slate-900">{overview.selected_month_label}</p>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <Button onClick={() => navigate('/tenants/add')} className="h-14 rounded-xl bg-primary hover:bg-primary/90 text-white font-semibold">
                    Add Tenant
                  </Button>
                  <Button onClick={() => navigate('/collect-rent')} className="h-14 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-semibold">
                    Collect Rent
                  </Button>
                  <Button onClick={() => navigate('/expenses')} className="h-14 rounded-xl bg-orange-600 hover:bg-orange-700 text-white font-semibold">
                    View Expenses
                  </Button>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </Layout>
  );
}
